import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao } from '@/componentes/Campo';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { Janela } from '@/componentes/JanelaDeAcao';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { apiDaRenovacao } from './api';
import { detalheDoEstudoAberto } from './renovacao';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

const HORIZONTES = ['12', '24', '36', '48', '60'] as const;

/**
 * ABRIR O ESTUDO de uma máquina (ferramenta 3): a máquina e o horizonte. O
 * sistema preenche o que sabe (a demanda, o diesel e a taxa do orçamento; o
 * preço médio pago; a operação da nova pelas máquinas do mesmo modelo), com a
 * fonte escrita — e NUNCA o valor de mercado, que é informado por quem estuda.
 * Ao abrir, a tela vai ao estudo.
 */
export function JanelaDoEstudoNovo({ aberta, aoFechar, equipamentoId = null }: { readonly aberta: boolean; readonly aoFechar: () => void; readonly equipamentoId?: string | null }) {
  const cliente = useQueryClient();
  const navegar = useNavigate();
  const opcoes = useQuery({ queryKey: ['renovacao', 'opcoes'], queryFn: apiDaRenovacao.opcoes, enabled: aberta && equipamentoId === null, meta: { oQueLia: 'as máquinas da frota' } });
  const comVisualDaFrota = useOpcoesComVisualDaFrota(aberta && equipamentoId === null);
  const [maquina, setMaquina] = useState(equipamentoId ?? '');
  const [horizonte, setHorizonte] = useState<(typeof HORIZONTES)[number]>('36');
  const [tentou, setTentou] = useState(false);
  const faltaMaquina = maquina ? null : 'Escolha a máquina do estudo.';
  const abrir = useMutation({
    mutationFn: () => apiDaRenovacao.criarEstudo(maquina, Number(horizonte)),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.criarEstudo>>>((r) => ({ titulo: 'Estudo aberto', ...detalheDoEstudoAberto(r) })) },
    onSuccess: async (r) => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['renovacao'] });
      void navegar({ to: '/equipamentos/renovacao/estudos/$estudoId', params: { estudoId: r.id } });
    },
  });
  return (
    <Janela
      titulo="Abrir estudo de renovação"
      descricao="O sistema preenche a demanda, o diesel e a taxa do orçamento, com a fonte. O valor de mercado é você que informa, com a cotação."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="p"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" carregando={abrir.isPending} onClick={() => { setTentou(true); if (!faltaMaquina) abrir.mutate(); }}>
            Abrir estudo
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {abrir.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(abrir.error).erro}. ${explicarFalha(abrir.error).comoResolver}`}</p> : null}
        {equipamentoId === null ? (
          opcoes.isPending ? (
            <ControleCarregando oQue="Carregando as máquinas" />
          ) : (
            <div className="flex flex-col gap-1">
              <CampoSelecao
                rotulo="Máquina"
                valor={maquina}
                aoMudar={setMaquina}
                rotuloVazio="Escolha a máquina"
                opcoes={comVisualDaFrota((opcoes.data?.maquinas ?? []).map((m) => opcaoDaMaquina(m)))}
                adesivo
              />
              {tentou && faltaMaquina ? <p className="txt-apoio text-parada-texto">{faltaMaquina}</p> : null}
            </div>
          )
        ) : null}
        <CampoSelecao
          rotulo="Horizonte"
          valor={horizonte}
          aoMudar={(v) => { setHorizonte(v as (typeof HORIZONTES)[number]); }}
          opcoes={HORIZONTES.map((h) => ({ valor: h, rotulo: `${h} meses` }))}
        />
      </div>
    </Janela>
  );
}
